import { motion } from "framer-motion";
import {
  MessageSquare,
  UserCheck,
  CreditCard,
  AlertTriangle,
  ShieldAlert,
  Headphones,
  FileText,
  RefreshCw,
  Scale,
  Mail,
  Clock,
  Globe,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut", delay },
  }),
};

// Helper to bold specific labels in extra text
const formatExtra = (text) => {
  const lines = text.split("\n");
  return lines.map((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) return null;
    const boldLabels = ["Opt-Out:", "Help:", "Email:", "Website:"];
    let bolded = trimmed;
    for (const label of boldLabels) {
      if (trimmed.startsWith(label)) {
        const rest = trimmed.slice(label.length);
        bolded = (
          <>
            <strong>{label}</strong>
            {rest}
          </>
        );
        break;
      }
    }
    return (
      <span key={idx} className="block">
        {bolded}
      </span>
    );
  });
};

// Helper to highlight company name with brand color
const highlightCompanyName = (text) => {
  const parts = text.split(/(MK AI Technologies|MK AI TECHNOLOGIES)/g);
  return parts.map((part, i) =>
    /MK AI Technologies/i.test(part) ? (
      <span key={i} className="text-[#8B4AFA] font-semibold">
        {part}
      </span>
    ) : (
      part
    )
  );
};

const sections = [
  // ----- NEW: Overview section -----
  {
    id: "overview",
    icon: FileText,
    title: "1. Overview",
   body: "By accessing or using our website (mkaitechnology.com) and our services, including AI solutions, software development services, and SMS communications, you agree to these Terms of Use.",
  },
  // ----- Existing sections (renumbered 2–11) -----
  {
    id: "sms-description",
    icon: MessageSquare,
    title: "2. SMS Program Description",
    body: "MK AI Technologies offers SMS messaging programs that may include:",
    list: [
      "Account notifications",
      "Customer support communications",
      "Project updates",
      "Appointment reminders",
      "Service notifications",
      "Promotional offers and updates (where consent is provided)",
    ],
  },
  {
    id: "opt-in-out",
    icon: UserCheck,
    title: "3. Opt-In & Opt-Out",
    body: "By opting into our SMS program, you agree to receive text messages as described above.",
    extra: "Opt-Out: Reply STOP at any time to unsubscribe.\nHelp: Reply HELP or contact contact@mkaitechnology.com.",
  },
  {
    id: "frequency-costs",
    icon: CreditCard,
    title: "4. Message Frequency & Costs",
    body: "Message frequency may vary. Standard message and data rates may apply depending on your wireless carrier and mobile plan.",
  },
  {
    id: "carrier-disclaimer",
    icon: AlertTriangle,
    title: "5. Carrier Disclaimer",
    body: "Wireless carriers are not liable for delayed or undelivered messages.",
  },
  {
    id: "eligibility",
    icon: ShieldAlert,
    title: "6. Eligibility (18+)",
    body: "You must be at least 18 years old to participate in our SMS programs or use services requiring SMS communications.",
  },
  {
    id: "customer-support",
    icon: Headphones,
    title: "7. Customer Support",
    body: "For SMS-related support or general inquiries, contact:",
    extra: "Email: contact@mkaitechnology.com\nWebsite: mkaitechnology.com",
  },
  {
    id: "privacy-policy",
    icon: FileText,
    title: "8. Privacy Policy",
    body: "Your participation in our SMS program is subject to our Privacy Policy. By opting in, you agree to the collection and use of your information as described in our Privacy Policy.",
  },
  {
    id: "modifications",
    icon: RefreshCw,
    title: "9. Modifications",
    body: "We reserve the right to modify these Terms of Use at any time. Any updates will be posted on this page with a revised Effective Date. Continued use of our Website or services after changes become effective constitutes acceptance of the updated Terms.",
  },
  {
    id: "governing-law",
    icon: Scale,
    title: "10. Governing Law",
    body: "These Terms of Use shall be governed by and construed in accordance with the laws applicable to MK AI Technologies, without regard to conflict of law principles.",
  },
  {
    id: "contact",
    icon: Mail,
    title: "11. Contact Us",
    body: "If you have any questions regarding these Terms of Use, please contact us:",
    contact: true,
  },
];

const TermsOfService = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  
  const tocTitle = (title) => title.replace(/^\d+\.\s*/, "");

  return (
    <section className="mt-40 bg-white">
      {/* Hero */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          className="mx-auto max-w-2xl border-b border-dotted border-[#8B4AFA] text-md tracking-widest text-slate-700"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
        >
          Legal &amp; Compliance
        </motion.div>

        <motion.h1
          className="mx-auto mt-6 max-w-4xl font-display text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 sm:text-6xl leading-tight"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.15}
        >
          Terms of Service
        </motion.h1>

        <motion.p
        className="mx-auto mt-6 max-w-3xl text-lg tracking-tight text-slate-600"
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        custom={0.3}
      >
        These Terms govern your access to and use of MK AI Technologies'
        services, including our AI solutions, software development, and
        SMS communications. Please read them carefully before continuing.
      </motion.p>

        <motion.div
          className="mb-6 mt-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-1.5 text-xs text-slate-500"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.4}
        >
          <Clock className="w-3.5 h-3.5" />
          Effective Date: August 5, 2026
        </motion.div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-24 pt-16">
        <div className="grid gap-10 lg:grid-cols-[380px_1fr]">
          {/* Table of Contents – without numbers */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 rounded-2xl border border-slate-100 bg-slate-50/60 p-6">
              <div className="mb-5 text-xs font-semibold uppercase tracking-widest text-slate-400">
                Table of Contents
              </div>
              <nav className="space-y-0.5">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className="group w-full rounded-lg px-4 py-2.5 text-left text-sm text-slate-600 transition-all duration-300 hover:bg-gradient-to-r hover:from-[#8B4AFA]/10 hover:to-[#8B4AFA]/5 hover:text-[#8B4AFA]"
                  >
                    <span className="flex items-center gap-3">
                      <span className="flex h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-300 transition-colors duration-300 group-hover:bg-[#8B4AFA]" />
                      <span className="relative whitespace-nowrap">
                        {tocTitle(s.title)}
                        <span className="absolute -bottom-0.5 left-0 h-[2px] w-0 bg-[#8B4AFA] transition-all duration-300 group-hover:w-full" />
                      </span>
                    </span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <div>
            {sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <motion.div
                  key={section.id}
                  id={section.id}
                  className="group mb-5 scroll-mt-28 rounded-2xl border border-slate-100 bg-white p-7 transition-all duration-300 hover:border-[#8B4AFA]/25 hover:shadow-lg hover:shadow-[#8B4AFA]/5 md:p-8 relative overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: Math.min(index * 0.03, 0.3) }}
                >
                  {/* Left accent line on hover */}
                  <div className="absolute left-0 top-0 h-full w-1 bg-[#8B4AFA] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                  <div className="flex items-start gap-4 relative">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#8B4AFA]/10 transition-all duration-300 group-hover:bg-[#8B4AFA]/15 group-hover:scale-105">
                      <Icon className="w-5 h-5 text-[#8B4AFA]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="mb-3 font-display text-xl font-semibold text-slate-900">
                        {section.title}
                      </h2>
                      {section.body && (
                        <p className="mb-3 text-base leading-relaxed text-slate-600">
                          {highlightCompanyName(section.body)}
                        </p>
                      )}
                      {section.list && (
                        <ul className="mt-2 space-y-2.5">
                          {section.list.map((item) => (
                            <li
                              key={item}
                              className="flex items-start gap-2.5 text-base leading-relaxed text-slate-600"
                            >
                              <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#8B4AFA]/50" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}
                      {section.extra && (
                        <div className="mt-3 text-base leading-relaxed text-slate-600">
                          {formatExtra(section.extra)}
                        </div>
                      )}
                      {section.contact && (
                        <div className="mt-4 rounded-2xl border border-[#8B4AFA]/10 bg-[#8B4AFA]/5 p-6">
                          <p className="mb-3 text-sm font-semibold text-slate-900">
                            MK AI Technologies
                          </p>
                          <div className="space-y-2.5">
                            <div className="flex items-center gap-2.5 text-sm text-slate-600">
                              <Mail className="w-4 h-4 text-[#8B4AFA]" />
                              <a
                                href="mailto:contact@mkaitechnology.com"
                                className="text-[#8B4AFA] hover:underline"
                              >
                                contact@mkaitechnology.com
                              </a>
                            </div>
                            <div className="flex items-center gap-2.5 text-sm text-slate-600">
                              <Globe className="w-4 h-4 text-[#8B4AFA]" />
                              <a
                                href="https://mkaitechnology.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#8B4AFA] hover:underline"
                              >
                                mkaitechnology.com
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TermsOfService;